<template>
  <div class="news-detail">
    <!-- 错误状态 - 只在加载完成且有错误时显示 -->
    <EmptyState 
      v-if="isLoaded && error"
      icon="error"
      title="Loading Failed"
      :description="error"
      :showAction="true"
      actionText="Back to News List"
      @action="goBack"
    />
    
    <!-- 新闻详情内容 -->
    <div v-else-if="article" class="news-detail-content">
      <!-- 社交媒体图标 -->
      <div class="social-icons">
        <a href="https://www.facebook.com/anovator.global" target="_blank" rel="noopener" aria-label="Facebook" class="social-icon">
          <img src="/static/images/facebook@2x.png" alt="Facebook" class="social-icon-img" />
        </a>
        <a href="https://www.instagram.com/anovator.global/" target="_blank" rel="noopener" aria-label="Instagram" class="social-icon">
          <img src="/static/images/instagram@2x.png" alt="Instagram" class="social-icon-img" />
        </a>
        <a href="https://www.linkedin.com/company/anovator/" target="_blank" rel="noopener" aria-label="LinkedIn" class="social-icon">
          <img src="/static/images/Linkedin@2x.png" alt="LinkedIn" class="social-icon-img" />
        </a>
        <a href="https://www.youtube.com/@anovator" target="_blank" rel="noopener" aria-label="YouTube" class="social-icon">
          <img src="/static/images/Youtube@2x.png" alt="YouTube" class="social-icon-img" />
        </a>
      </div>
      
      <!-- 新闻标题 -->
      <h1 class="news-title">{{ article.title }}</h1>
      
      <!-- 新闻元信息 -->
      <div class="news-author-date">
        {{ article.author || 'The ANOVATOR Team' }}, {{ formatDate(article.publishTime) }}
        <span v-if="article.viewCount > 0" class="ml-3">
          <i class="fa fa-eye"></i> {{ article.viewCount }}
        </span>
      </div>
      
      <!-- 新闻内容 -->
      <div class="news-content ql-editor" v-html="article.content"></div>
      
      <!-- 返回按钮 -->
      <div class="news-navigation">
        <div 
          @click="goToPrevious" 
          class="nav-button prev-button"
          :class="{ 'disabled': !previousArticle, 'transitioning': isTransitioning }"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="m15 18-6-6 6-6"/>
          </svg>
          <span v-if="!isTransitioning">Previous article</span>
          <span v-else class="loading-text">Loading...</span>
        </div>
        
        <div 
          @click="goToNext" 
          class="nav-button next-button"
          :class="{ 'disabled': !nextArticle, 'transitioning': isTransitioning }"
        >
          <span v-if="!isTransitioning">Next article</span>
          <span v-else class="loading-text">Loading...</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="m9 18 6-6-6-6"/>
          </svg>
        </div>
      </div>
      
      <!-- 相关新闻推荐 -->
      <div v-if="relatedNews && relatedNews.length > 0" class="related-news">
        <h3>Related News</h3>
        <div class="related-news-list">
          <div 
            v-for="item in relatedNews" 
            :key="item.id"
            class="related-news-item"
            @click="viewRelatedNews(item)"
          >
            <img v-if="item.imageUrl" :src="getImagePath(item.imageUrl)" :alt="item.title" class="related-news-image" />
            <div class="related-news-info">
              <h4>{{ item.title }}</h4>
              <p class="related-news-date">{{ formatDate(item.publishTime) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 未找到新闻 - 只在加载完成且没有错误但也没有数据时显示 -->
    <EmptyState 
      v-else-if="isLoaded && !error && !article"
      icon="search"
      title="News Not Found"
      description="The news article you are looking for may have been deleted or does not exist"
      :showAction="true"
      actionText="Back to News List"
      @action="goBack"
    />
  </div>
</template>

<script>
import EmptyState from '@/components/EmptyState.vue'
import { newsAPI } from '@/utils/api'
import { imgPath } from '@/utils/main'

export default {
  name: 'NewsDetail',
  components: {
    EmptyState
  },
  data() {
    return {
      article: null,
      relatedNews: [],
      error: null,
      isLoaded: false,
      previousArticle: null,
      nextArticle: null,
      allArticles: [],
      // 切换状态管理
      isTransitioning: false
    }
  },
  async mounted() {
    await this.fetchNewsDetail()
  },
  watch: {
    '$route'(to, from) {
      if (to.params.id !== from.params.id) {
        this.fetchNewsDetail()
      }
    }
  },
  methods: {
    async fetchNewsDetail() {
      try {
        this.error = null
        this.isLoaded = false
        
        const newsId = this.$route.params.id
        if (!newsId) {
          this.error = 'Invalid news ID'
          this.isLoaded = true
          return
        }
        
        // 获取新闻详情
        const response = await newsAPI.getNewsDetail(newsId)
        
        if (response && response.DATA) {
          this.article = response.DATA
          
          // 获取所有新闻列表用于上一篇/下一篇导航
          await this.fetchAllNews()
          
          // 获取相关新闻（可选）
          await this.fetchRelatedNews()
        } else {
          this.error = 'Failed to retrieve news details'
        }
      } catch (error) {
        console.error('获取新闻详情失败:', error)
        this.error = 'Network connection failed, please try again later'
      } finally {
        this.isLoaded = true
      }
    },
    
    async fetchRelatedNews() {
      try {
        // 获取相关新闻，这里可以根据分类或标签获取
        const response = await newsAPI.getNewsList({ limit: 3 })
        if (response && response.data && response.data.list) {
          // 过滤掉当前新闻
          this.relatedNews = response.data.list
            .filter(item => item.id !== this.article.id)
            .slice(0, 3)
        }
      } catch (error) {
        console.error('获取相关新闻失败:', error)
        // 相关新闻获取失败不影响主要功能
      }
    },
    
    async fetchAllNews() {
      try {
        // 获取所有新闻列表
        const response = await newsAPI.getNewsList({
          isPublished: 1,
          isDeleted: '0',
          page: 1,
          size: 100 // 获取足够多的新闻用于导航
        })
        
        if (response && response.DATA && Array.isArray(response.DATA)) {
          this.allArticles = response.DATA.map(item => ({
            ...item,
            publishTime: item.publishTime ? new Date(item.publishTime) : new Date(),
            gmtCreate: item.gmtCreate ? new Date(item.gmtCreate) : new Date(),
            gmtModify: item.gmtModify ? new Date(item.gmtModify) : new Date()
          }))
          
          // 按发布时间排序
          this.allArticles.sort((a, b) => new Date(b.publishTime) - new Date(a.publishTime))
          
          // 找到当前文章的位置
          this.findPreviousAndNext()
        }
      } catch (error) {
        console.error('获取新闻列表失败:', error)
      }
    },
    
    findPreviousAndNext() {
      if (!this.article || !this.allArticles.length) return
      
      const currentIndex = this.allArticles.findIndex(item => item.id === this.article.id)
      
      if (currentIndex !== -1) {
        // 上一篇（更新的文章）
        this.previousArticle = currentIndex > 0 ? this.allArticles[currentIndex - 1] : null
        // 下一篇（更旧的文章）
        this.nextArticle = currentIndex < this.allArticles.length - 1 ? this.allArticles[currentIndex + 1] : null
      }
    },
    
    formatDate(dateObj) {
      try {
        let d;
        if (dateObj instanceof Date) {
          d = dateObj;
        } else {
          d = new Date(dateObj);
        }
        const options = { year: 'numeric', month: 'long', day: 'numeric' }
        return d.toLocaleDateString('en-US', options)
      } catch (e) {
        return dateObj
      }
    },
    
    goBack() {
      this.$router.push('/news')
    },
    
    async goToPrevious() {
      if (this.previousArticle && !this.isTransitioning) {
        this.isTransitioning = true
        
        // 添加淡出效果
        const contentElement = document.querySelector('.news-detail-content')
        if (contentElement) {
          contentElement.style.opacity = '0.5'
          contentElement.style.transition = 'opacity 0.3s ease'
        }
        
        // 延迟一点时间让用户看到状态变化
        await new Promise(resolve => setTimeout(resolve, 150))
        
        try {
          await this.$router.push(`/news/${this.previousArticle.id}`)
        } finally {
          // 确保状态重置
          setTimeout(() => {
            this.isTransitioning = false
            if (contentElement) {
              contentElement.style.opacity = '1'
            }
          }, 100)
        }
      }
    },
    
    async goToNext() {
      if (this.nextArticle && !this.isTransitioning) {
        this.isTransitioning = true
        
        // 添加淡出效果
        const contentElement = document.querySelector('.news-detail-content')
        if (contentElement) {
          contentElement.style.opacity = '0.5'
          contentElement.style.transition = 'opacity 0.3s ease'
        }
        
        // 延迟一点时间让用户看到状态变化
        await new Promise(resolve => setTimeout(resolve, 150))
        
        try {
          await this.$router.push(`/news/${this.nextArticle.id}`)
        } finally {
          // 确保状态重置
          setTimeout(() => {
            this.isTransitioning = false
            if (contentElement) {
              contentElement.style.opacity = '1'
            }
          }, 100)
        }
      }
    },
    
    viewRelatedNews(item) {
      this.$router.push(`/news/${item.id}`)
    },
    
    // 获取图片路径
    getImagePath(img) {
      return imgPath(img)
    }
  }
}
</script>

<style scoped>
/* 引入Quill编辑器样式 */
@import '../assets/css/quill.snow.css';

.news-detail {
  background-color: #000000;
  margin-top: 80px;
  min-height: 100vh;
  color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.news-detail-content {
  max-width: 1024px;
  margin: 0 auto;
  padding: 60px 0 40px;
  position: relative;
}

/* 社交媒体图标 */
.social-icons {
  display: flex;
  flex-direction: row;
  justify-content: left;
  gap: 20px;
  margin-bottom: 30px;
  z-index: 10;
}

.social-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: transform 0.2s ease, border-color 0.2s ease;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.social-icon:hover {
  transform: translateY(-2px);
  background: rgba(255, 255, 255, 0.2);
}

.social-icon-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* 新闻标题 */
.news-title {
  font-size: 2.7rem;
  font-weight: 700;
  line-height: 1.2;
  color: #ffffff;
  margin: 0 0 20px 0;
  text-align: left;
  max-width: 100%;
}

/* 新闻元信息 */
.news-author-date {
  text-align: left;
  margin-bottom: 40px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
}

.ml-3 {
  margin-left: 1rem;
}

/* 新闻图片 */
.news-image-container {
  margin: 40px 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.news-main-image {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}

/* 新闻内容 */
.news-content {
  font-size: 16px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 60px;
  max-width: 100%;
}

/* 覆盖Quill编辑器样式以适应深色主题 */
.news-content.ql-editor {
  padding: 0;
  color: rgba(255, 255, 255, 0.9);
  background: transparent;
  border: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

.news-content.ql-editor p {
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 20px 0;
}

.news-content.ql-editor h1,
.news-content.ql-editor h2,
.news-content.ql-editor h3,
.news-content.ql-editor h4,
.news-content.ql-editor h5,
.news-content.ql-editor h6 {
  color: #FFFFFF;
  margin: 30px 0 20px 0;
}

.news-content.ql-editor h1 {
  font-size: 2.5rem;
}

.news-content.ql-editor h2 {
  font-size: 2rem;
}

.news-content.ql-editor h3 {
  font-size: 1.5rem;
}

.news-content.ql-editor strong {
  color: #FFFFFF;
}

.news-content.ql-editor em {
  color: rgba(255, 255, 255, 0.95);
}

.news-content.ql-editor ul,
.news-content.ql-editor ol {
  color: rgba(255, 255, 255, 0.9);
  margin: 20px 0;
}

.news-content.ql-editor li {
  margin: 8px 0;
}

.news-content.ql-editor blockquote {
  border-left: 4px solid #EE0B6A;
  color: rgba(255, 255, 255, 0.95);
  background: rgba(238, 11, 106, 0.1);
  padding: 20px;
  margin: 30px 0;
  border-radius: 8px;
}

.news-content.ql-editor a {
  color: #EE0B6A;
  text-decoration: none;
}

.news-content.ql-editor a:hover {
  text-decoration: underline;
}

.news-content.ql-editor img {
  max-width: 100%;
  height: auto;
  border-radius: 12px;
  margin: 30px 0;
}

.news-content.ql-editor pre {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 20px;
  color: rgba(255, 255, 255, 0.9);
  overflow-x: auto;
}

.news-content.ql-editor code {
  background: rgba(255, 255, 255, 0.1);
  color: #EE0B6A;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
}

/* 导航按钮 */
.news-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 60px;
  gap: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
}

.nav-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  border: none;
  border-radius: 8px;
  color: #ffffff;
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 140px;
  justify-content: center;
}

.nav-button.disabled {
  opacity: 0.4;
  pointer-events: none;
  color: #ffffff;
  cursor: no-drop;
}

.nav-button.transitioning {
  opacity: 0.6;
  pointer-events: none;
  cursor: wait;
}

.nav-button.transitioning svg {
  animation: pulse 1s infinite;
}

.loading-text {
  font-style: italic;
  opacity: 0.8;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

.news-detail-content {
  transition: opacity 0.3s ease;
}
.nav-button{
  text-align: right;
  justify-content: flex-end;
  color: #EE0B6A;
}
.prev-button {
  text-align: left;
  justify-content: flex-start;
  color: #EE0B6A;
}
.prev-button svg {
  margin-right: 4px;
}

.next-button svg {
  margin-left: 4px;
}

/* 相关新闻 */
.related-news {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 60px;
  max-width: 100%;
}

.related-news h3 {
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 30px;
  color: #ffffff;
}

.related-news-list {
  display: grid;
  gap: 25px;
}

.related-news-item {
  display: flex;
  gap: 20px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.02);
}

.related-news-item:hover {
  border-color: rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.05);
  transform: translateY(-2px);
}

.related-news-image {
  width: 100px;
  height: 75px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
}

.related-news-info {
  flex: 1;
}

.related-news-info h4 {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #ffffff;
  line-height: 1.4;
}

.related-news-date {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
}

/* 响应式设计 */
/* 平板端优化 (768px - 1024px) */
@media (min-width: 769px) and (max-width: 1024px) {
  .news-detail-content {
    padding: 50px 30px 30px;
    max-width: 800px;
  }
  
  .social-icons {
    left: 30px;
    gap: 12px;
  }
  
  .social-icon {
    width: 40px;
    height: 40px;
  }
  
  .news-title {
    font-size: 2.5rem;
  }
  
  .news-content {
    max-width: 100%;
  }
  
  .news-navigation {
    flex-direction: column;
    gap: 15px;
  }
  
  .nav-button {
    width: 100%;
    min-width: auto;
  }
  
  .related-news {
    max-width: 100%;
  }
}

/* 手机端优化 (最大768px) */
@media (max-width: 768px) {
  .news-detail {
    margin-top: 60px;
  }
  
  .news-detail-content {
    padding: 50px 20px 20px;
  }
  
  .social-icons {
    flex-direction: row;
    margin-bottom: 25px;
    gap: 16px;
  }
  
  .social-icon {
    width: 40px;
    height: 40px;
  }
  
  .news-title {
    font-size: 2.2rem;
    padding-left: 0;
    max-width: 100%;
    margin-bottom: 20px;
  }
  
  .news-author-date {
    padding-left: 0;
    margin-bottom: 25px;
  }
  
  .news-image-container {
    margin-bottom: 25px;
  }
  
  .news-main-image {
    border-radius: 12px;
  }
  
  .news-content {
    padding-left: 0;
    max-width: 100%;
    font-size: 16px;
    line-height: 1.7;
  }
  
  .news-navigation {
    flex-direction: column;
    gap: 15px;
    margin: 30px 0;
  }
  
  .nav-button {
    width: 100%;
    min-width: auto;
  }
  
  .related-news {
    padding-left: 0;
    max-width: 100%;
  }
  
  .related-news-item {
    flex-direction: column;
    padding: 15px;
    margin-bottom: 15px;
  }
  
  .related-news-image {
    width: 100%;
    height: 150px;
    margin-bottom: 12px;
  }
  
  .related-news-info h4 {
    font-size: 15px;
    margin-bottom: 6px;
  }
  
  .related-news-date {
    font-size: 11px;
  }
}

/* 小屏手机优化 (最大480px) */
@media (max-width: 480px) {
  .news-detail-content {
    padding: 50px 20px 20px;
  }
  
  .social-icons {
    gap: 12px;
  }
  
  .social-icon {
    width: 36px;
    height: 36px;
  }
  
  .news-title {
    font-size: 1.8rem;
    line-height: 1.3;
  }
  
  .news-author-date {
    font-size: 13px;
  }
  
  .news-content {
    font-size: 15px;
    line-height: 1.6;
  }
  
  .related-news-item {
    padding: 12px;
  }
  
  .related-news-image {
    height: 120px;
    border-radius: 6px;
  }
  
  .related-news-info h4 {
    font-size: 14px;
  }
}
</style>